import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Minus, Plus, ArrowLeft, MessageCircle } from "lucide-react";
import ShareButtons from "@/components/ShareButtons";
import ProductGallery from "@/components/ProductGallery";
import ProductReviews from "@/components/ProductReviews";
import { StarRating } from "@/components/StarRating";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [wa, setWa] = useState("");
  const [summary, setSummary] = useState({ average: 0, count: 0 });

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => setP(data)).catch(() => nav("/shop"));
    api.get("/settings").then(({ data }) => setWa(data.whatsapp_number || "")).catch(() => {});
  }, [id, nav]);

  if (!p) return <main className="p-12 text-center font-display text-2xl">Loading…</main>;

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
      <button onClick={() => nav(-1)} className="text-sm text-muted-foreground flex items-center gap-1 mb-6 hover:text-primary" data-testid="pd-back">
        <ArrowLeft size={14} /> Back
      </button>
      {/* min-w-0 keeps each column inside its track: a grid item's automatic minimum
          is its min-content width, so one long unbreakable string used to widen the
          whole page (and with it the product photo) past the screen. */}
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <div className="min-w-0">
          <ProductGallery images={p.images?.length ? p.images : (p.image ? [p.image] : [])} name={p.name} />
        </div>
        <div className="min-w-0">
          <Badge variant="outline" className="border-secondary/30 text-secondary capitalize mb-3">{p.category}</Badge>
          <h1 className="font-display text-5xl mb-3">{p.name}</h1>
          <div className="text-2xl text-primary font-medium mb-3">₹{p.price?.toFixed(2)}</div>
          <div className="mb-6"><StarRating {...summary} testid="pd-rating" /></div>
          <p className="text-muted-foreground leading-relaxed mb-6">{p.description}</p>

          {p.benefits?.length > 0 && (
            <div className="mb-6">
              <div className="overline mb-2">Benefits</div>
              <ul className="list-disc pl-5 text-sm text-foreground/80 space-y-1">
                {p.benefits.map((b, i) => <li key={`${b}-${i}`}>{b}</li>)}
              </ul>
            </div>
          )}
          {p.dosage && (
            <div className="mb-6">
              <div className="overline mb-2">Suggested Dosage</div>
              <p className="text-sm">{p.dosage}</p>
            </div>
          )}

          <div className="flex items-center gap-4 mt-8">
            <div className="flex items-center border border-border rounded-full">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5 hover:text-primary" data-testid="pd-qty-dec"><Minus size={14}/></button>
              <span className="px-4 font-medium" data-testid="pd-qty">{qty}</span>
              <button
                onClick={() => setQty(Math.min(p.stock || 1, qty + 1))}
                disabled={qty >= (p.stock || 0)}
                className="p-2.5 hover:text-primary disabled:opacity-40"
                data-testid="pd-qty-inc"
              ><Plus size={14}/></button>
            </div>
            <Button onClick={() => { add(p, qty); toast.success("Added to cart"); }} disabled={p.stock <= 0} className="btn-action h-12 px-7" data-testid="pd-add-cart">
              Add to Cart
            </Button>
            <Button variant="outline" onClick={() => { add(p, qty); nav("/cart"); }} disabled={p.stock <= 0} className="btn-pill h-12 px-7 border-primary/30" data-testid="pd-buy-now">
              Buy Now
            </Button>
          </div>
          <div className="mt-3 text-xs" data-testid="pd-stock">
            {p.stock <= 0
              ? <span className="text-destructive">Out of stock — message us on WhatsApp and we&apos;ll restock for you</span>
              : p.stock <= 5
                ? <span className="text-secondary">Only {p.stock} left in stock</span>
                : <span className="text-muted-foreground">{p.stock} in stock</span>}
          </div>
          {wa && (
            <Button
              onClick={() => {
                const num = wa.replace(/\D/g, "");
                const productUrl = `${window.location.origin}/shop/${p.id}`;
                const msg =
                  `Namaste! I'd like to enquire about *${p.name}* from Immortal Vedas.\n\n` +
                  `• Product: ${p.name}\n` +
                  `• Category: ${p.category}\n` +
                  `• Price: ₹${p.price?.toFixed(2)}\n` +
                  `• Quantity: ${qty}\n\n` +
                  `Product link: ${productUrl}\n\n` +
                  `Please confirm if this is available in stock.`;
                window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
              }}
              variant="outline"
              className="btn-pill h-11 mt-3 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"
              data-testid="pd-whatsapp"
            >
              <MessageCircle size={14} className="mr-2" /> Order / Enquire on WhatsApp
            </Button>
          )}

          <ShareButtons productId={p.id} productName={p.name} price={p.price} />
        </div>
      </div>

      <ProductReviews productId={p.id} onSummary={setSummary} />
    </main>
  );
}
